import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getChats } from '../Reducers/actions/chatActions';
import { fetchApi } from '../../helpers/fetch';
import useAdaptivePolling from './useAdaptivePolling';

const useConversationsPolling = () => {
  const dispatch = useDispatch();
  const currentChats = useSelector((state) => state.chat.chats);
  const lastDataRef = useRef(null);
  const pollingRef = useRef(null);

  // Helper function to compare conversations data
  const hasConversationsChanged = (newData, oldData) => {
    if (!oldData) return true; // First time, always update
    if (!newData) return false;
    
    // Quick length check
    if (newData.length !== oldData.length) return true;
    
    // Sort both arrays by ID for consistent comparison
    const sortById = (chats) => [...chats].sort((a, b) => a.id - b.id);
    const newSorted = sortById(newData);
    const oldSorted = sortById(oldData);
    
    // Compare each conversation
    for (let i = 0; i < newSorted.length; i++) {
      const newChat = newSorted[i];
      const oldChat = oldSorted[i];
      
      if (!oldChat || newChat.id !== oldChat.id) return true;
      
      // Check message count
      const newMessageCount = newChat.messages?.length || 0;
      const oldMessageCount = oldChat.messages?.length || 0;
      if (newMessageCount !== oldMessageCount) return true;
      
      // Check last message timestamp
      const newLastMessage = newChat.messages?.[newMessageCount - 1];
      const oldLastMessage = oldChat.messages?.[oldMessageCount - 1];
      
      const newTimestamp = newLastMessage?.created_at || newChat.updated_at || newChat.created_at;
      const oldTimestamp = oldLastMessage?.created_at || oldChat.updated_at || oldChat.created_at;
      
      if (newTimestamp !== oldTimestamp) return true;
      
      // Check unread status if available
      if (newChat.unread_count !== oldChat.unread_count) return true;
    }
    
    return false;
  };

  // Helper function to detect new activity (new messages, not just reordering)
  const checkForNewActivity = (newData, oldData) => {
    if (!oldData || !newData) return false;
    
    // Check for new messages by comparing total message counts and timestamps
    for (const newChat of newData) {
      const oldChat = oldData.find(c => c.id === newChat.id);
      if (!oldChat) {
        // Completely new chat
        return true;
      }
      
      const newMessageCount = newChat.messages?.length || 0;
      const oldMessageCount = oldChat.messages?.length || 0;
      
      if (newMessageCount > oldMessageCount) {
        // New messages in existing chat
        return true;
      }
      
      // Check for newer timestamps even with same message count (edited messages, etc.)
      const newLastMessage = newChat.messages?.[newMessageCount - 1];
      const oldLastMessage = oldChat.messages?.[oldMessageCount - 1];
      
      if (newLastMessage && oldLastMessage) {
        const newTimestamp = new Date(newLastMessage.created_at || newLastMessage.updated_at);
        const oldTimestamp = new Date(oldLastMessage.created_at || oldLastMessage.updated_at);
        
        if (newTimestamp > oldTimestamp) {
          return true;
        }
      }
    }
    
    return false;
  };

  const pollForConversations = async () => {
    // Fetch data directly without dispatching to compare first
    const data = await fetchApi('/api/chats/user');
    
    if (!data.success) {
      throw new Error(`API request failed: ${data.message || 'Unknown error'}`);
    }
    
    const newConversations = data.data || [];
    const hasChanges = hasConversationsChanged(newConversations, lastDataRef.current);
    
    if (hasChanges) {
      // Check if we have truly new activity (new messages, not just order changes)
      const hasNewActivity = checkForNewActivity(newConversations, lastDataRef.current);
      if (hasNewActivity) {
        // Trigger fast polling when we detect new messages in conversations
        if (pollingRef.current?.triggerFastPoll) {
          pollingRef.current.triggerFastPoll();
        }
      }
      
      // Dispatch the action to update Redux store
      const result = await dispatch(getChats());
      
      if (result && result.success) {
        lastDataRef.current = newConversations;
      }
    }
  };

  // Initialize lastDataRef with current Redux state
  if (currentChats && !lastDataRef.current) {
    lastDataRef.current = currentChats;
  }

  // Set up adaptive polling for conversations page only
  const adaptivePolling = useAdaptivePolling({
    pollingFunction: pollForConversations,
    enabledRoutes: ['/conversations'],
    context: 'conversations'
  });

  // Store polling reference for triggering fast polling from within pollForConversations
  pollingRef.current = adaptivePolling;

  // Return utility functions for manual operations
  return {
    ...adaptivePolling,
    pollNow: adaptivePolling.pollNow,
    stopPolling: adaptivePolling.stopPolling
  };
};

export default useConversationsPolling;